import React, { useEffect, useRef, useState } from "react"
import { Badge, Box, Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from "@chakra-ui/react"
import { getDoctorById, uploadDoctorDocument, saveDoctorDocument, verifyDoctorDocument, getDoctorDocumentSignedUrl } from "../../Http/Index"
import { ShowAlert } from "../../GlobalComponent/Alerts/Alert"
import { RegistryDoctor, DoctorSelectorBanner, Label } from "./shared.types"

interface DocItem {
  id: string; title: string; fileName: string | null; fileSize: string | null
  uploadedAt: string | null; fileType: "pdf" | "image" | null
  status: "verified" | "pending" | "missing"; approved: boolean
  fileUrl: string | null; _docId?: string
}

const toSlug = (name: string) => name.trim().toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")

const DEFAULT_DOCS = [
  { id: "medical-license", title: "Medical License" },
  { id: "medical-degree", title: "Medical Degree" },
  { id: "board-certification", title: "Board Certifications" },
]

const emptyDoc = (id: string, title: string): DocItem => ({
  id, title, fileName: null, fileSize: null, uploadedAt: null,
  fileType: null, status: "missing", approved: false, fileUrl: null,
})

const apiDocToItem = (doc: any): DocItem => {
  const ext = doc.document_url?.split(".").pop()?.toLowerCase() ?? ""
  return {
    id: doc.document_type,
    title: doc.document_type.replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase()),
    fileName: doc.document_url?.split("/").pop() ?? null, fileSize: null,
    uploadedAt: doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null,
    fileType: ext === "pdf" ? "pdf" : "image",
    status: doc.verified ? "verified" : "pending",
    approved: doc.verified ?? false, fileUrl: doc.document_url ?? null, _docId: doc._id,
  }
}

const STATUS_CFG = {
  verified: { dot: "#14b8a6", label: "Admin Approval",   bg: "gray.50",  border: "gray.200",  labelColor: "gray.500" },
  pending:  { dot: "#f59e0b", label: "Approve Document", bg: "gray.50",  border: "gray.200",  labelColor: "gray.500" },
  missing:  { dot: "#ba1a1a", label: "Missing",          bg: "red.50",   border: "red.200",   labelColor: "red.700"  },
}

const PROGRESS_CFG = {
  verified: { color: "#14b8a6", icon: "✓", statusLabel: "Verified" },
  pending:  { color: "#f59e0b", icon: "⏳", statusLabel: "Pending" },
  missing:  { color: "#c3c6d6", icon: "○", statusLabel: "Not Uploaded" },
}

const ToggleSwitch: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <Box as="button" role="switch" aria-checked={checked} onClick={onChange}
    position="relative" display="inline-flex" h="24px" w="44px"
    alignItems="center" borderRadius="full"
    bg={checked ? "blue.600" : "gray.300"} transition="background 0.2s" flexShrink={0}>
    <Box position="absolute" left={checked ? "22px" : "2px"} w="16px" h="16px" borderRadius="full" bg="white" transition="left 0.2s" />
  </Box>
)

interface Props {
  doctors: RegistryDoctor[]
  selected: RegistryDoctor | null
  onSelectDoctor: (doc: RegistryDoctor) => void
  onGoToRegistry: () => void
  onGoToVerification: () => void
  onRefreshDoctors: () => void
  refreshingDoctors: boolean
}

export const Documents: React.FC<Props> = ({ doctors, selected, onSelectDoctor, onGoToRegistry, onGoToVerification, onRefreshDoctors, refreshingDoctors }) => {
  const [docs, setDocs] = useState<DocItem[]>([])
  const [uploadingId, setUploadingId] = useState<string | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)
  const [loadingDocs, setLoadingDocs] = useState(false)
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [newDocName, setNewDocName] = useState("")
  const [pendingUpload, setPendingUpload] = useState<{ docType: string; file: File } | null>(null)
  const fileRefs = useRef<Record<string, HTMLInputElement | null>>({})
  const newDocFileRef = useRef<HTMLInputElement | null>(null)

  const loadDocs = async (doctorId: string) => {
    try {
      setLoadingDocs(true)
      const { data } = await getDoctorById(doctorId)
      const apiDocs: any[] = data?.data?.documents ?? data?.documents ?? []
      const byType: Record<string, any> = {}
      apiDocs.forEach((d: any) => { byType[d.document_type] = d })
      const defaultItems = DEFAULT_DOCS.map((def) => byType[def.id] ? apiDocToItem(byType[def.id]) : emptyDoc(def.id, def.title))
      const extraItems = Object.entries(byType).filter(([type]) => !DEFAULT_DOCS.some((d) => d.id === type)).map(([, doc]) => apiDocToItem(doc))
      setDocs([...defaultItems, ...extraItems])
    } catch { setDocs(DEFAULT_DOCS.map((d) => emptyDoc(d.id, d.title))) }
    finally { setLoadingDocs(false) }
  }

  useEffect(() => {
    if (!selected) { setDocs(DEFAULT_DOCS.map((d) => emptyDoc(d.id, d.title))); return }
    loadDocs(selected._id)
  }, [selected?._id]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleApproval = async (id: string) => {
    if (!selected) return
    const doc = docs.find((d) => d.id === id)
    if (!doc?._docId) { ShowAlert("warning", "Document not yet uploaded"); return }
    try {
      const newVal = !doc.approved
      await verifyDoctorDocument(selected._id, doc._docId, newVal)
      setDocs((prev) => prev.map((d) => d.id === id ? { ...d, approved: newVal, status: newVal ? "verified" : "pending" } : d))
    } catch (e: any) { ShowAlert("error", e?.response?.data?.message || "Verification update failed") }
  }

  const handleUpload = async (docType: string, file: File) => {
    if (!selected) return
    try {
      setUploadingId(docType)
      const ext = file.name.split(".").pop() ?? "pdf"
      const docName = `${docType}.${ext}`
      await uploadDoctorDocument(file, selected._id, docName)
      await saveDoctorDocument({ doctorId: selected._id, documentType: docType, documentUrl: `doctor-documents/${selected._id}/${docName}` })
      ShowAlert("success", "Document uploaded successfully")
      await loadDocs(selected._id)
    } catch (e: any) { ShowAlert("error", e?.response?.data?.message || "Upload failed") }
    finally { setUploadingId(null) }
  }

  const confirmAddDoc = async () => {
    if (!pendingUpload) return
    await handleUpload(pendingUpload.docType, pendingUpload.file)
    setPendingUpload(null); setNewDocName(""); setAddModalOpen(false)
  }

  const verifiedCount = docs.filter((d) => d.status === "verified").length
  const total = docs.length
  const circumference = 2 * Math.PI * 45
  const offset = circumference - (verifiedCount / total) * circumference

  return (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", xl: "8fr 4fr" }} gap={6} mt={4}>
      <Box gridColumn="1 / -1">
        <DoctorSelectorBanner doctors={doctors} selected={selected} onSelect={onSelectDoctor} onGoToRegistry={onGoToRegistry} onRefresh={onRefreshDoctors} refreshing={refreshingDoctors} />
      </Box>

      {!selected ? (
        <Box gridColumn="1 / -1" p={8} border="1px dashed" borderColor="gray.200" borderRadius="md" textAlign="center">
          <Text fontSize="lg" mb={2}>📄</Text>
          <Text fontWeight="semibold" mb={1}>No Doctor Selected</Text>
          <Text fontSize="sm" color="gray.500">Select a doctor above or go to Registry and click 📄 Docs.</Text>
        </Box>
      ) : (
        <React.Fragment>
          <Box border="1px solid" borderColor="gray.200" borderRadius="lg" bg="white" shadow="sm" overflow="hidden">
            <Box px={4} py={3} bg="gray.50" borderBottom="1px solid" borderColor="gray.200"
              display="flex" justifyContent="space-between" alignItems="center">
              <Text fontWeight="semibold" fontSize="sm">Documents for {selected.name}</Text>
              <Box display="flex" alignItems="center" gap={3}>
                {loadingDocs && <Text fontSize="xs" color="gray.400">Loading...</Text>}
                <Badge colorScheme={verifiedCount === total && total > 0 ? "green" : "orange"} fontSize="xs">{verifiedCount}/{total} Approved</Badge>
                <Button size="sm" colorScheme="blue" onClick={() => setAddModalOpen(true)}>+ Add Document</Button>
              </Box>
            </Box>

            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#fafafa", borderBottom: "1px solid #e2e8f0" }}>
                  {["Document", "Status", "File", "Actions", "Approved"].map((h) => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left" }}>
                      <Text fontSize="xs" color="gray.500" fontWeight="semibold">{h}</Text>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {docs.map((doc) => {
                  const cfg = STATUS_CFG[doc.status]
                  return (
                    <tr key={doc.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "14px 16px" }}>
                        <Text fontWeight="semibold" fontSize="sm">{doc.title}</Text>
                        <Text fontSize="xs" color="gray.400" fontFamily="monospace">{doc.id}</Text>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <Box display="inline-flex" alignItems="center" gap={1} bg={cfg.bg} px={2} py={1} borderRadius="full" border="1px solid" borderColor={cfg.border}>
                          <Box w={2} h={2} borderRadius="full" bg={cfg.dot} />
                          <Text fontSize="xs" fontWeight="semibold" color={cfg.labelColor}>{cfg.label}</Text>
                        </Box>
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        {doc.fileName ? (
                          <Box><Text fontSize="sm" fontWeight="medium">{doc.fileName}</Text><Text fontSize="xs" color="gray.400">{doc.uploadedAt}</Text></Box>
                        ) : <Text fontSize="xs" color="gray.400" fontStyle="italic">No file uploaded</Text>}
                      </td>
                      <td style={{ padding: "14px 16px" }}>
                        <Box display="flex" gap={2} alignItems="center">
                          <Button size="sm" colorScheme="blue" variant="outline" isLoading={uploadingId === doc.id} loadingText="Uploading"
                            onClick={() => fileRefs.current[doc.id]?.click()} isDisabled={!!uploadingId && uploadingId !== doc.id}>
                            {doc.fileName ? "Re-upload" : "Upload"}
                          </Button>
                          <input type="file" accept=".pdf,image/*" hidden ref={(el) => { fileRefs.current[doc.id] = el }}
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleUpload(doc.id, f); e.target.value = "" }} />
                          {doc.fileUrl && (
                            <Button size="sm" colorScheme="gray" variant="outline" isLoading={viewingId === doc.id}
                              onClick={async () => {
                                try {
                                  setViewingId(doc.id)
                                  const parts = doc.fileUrl!.replace(/\/\//g, "/").split("/")
                                  const docName = parts.pop()!; const docFolder = parts.join("/")
                                  const res = await getDoctorDocumentSignedUrl(docFolder, docName)
                                  window.open(URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] as string | undefined })), "_blank")
                                } catch { ShowAlert("error", "Failed to open document") }
                                finally { setViewingId(null) }
                              }}>View</Button>
                          )}
                        </Box>
                      </td>
                      <td style={{ padding: "14px 16px" }}><ToggleSwitch checked={doc.approved} onChange={() => toggleApproval(doc.id)} /></td>
                    </tr>
                  )
                })}
              </tbody>
            </table>

            <Box px={4} py={3} borderTop="1px solid" borderColor="gray.100" display="flex" justifyContent="flex-end">
              <Button size="sm" colorScheme="blue" isDisabled={total === 0 || verifiedCount < total} onClick={onGoToVerification}>
                Proceed to Verification →
              </Button>
            </Box>
          </Box>

          {/* Add Document Modal */}
          <Modal isOpen={addModalOpen} onClose={() => { setAddModalOpen(false); setNewDocName(""); setPendingUpload(null) }} isCentered size="sm">
            <ModalOverlay />
            <ModalContent>
              <ModalHeader fontSize="md">Add New Document</ModalHeader>
              <ModalBody display="flex" flexDir="column" gap={4}>
                <Box>
                  <Label required>Document Name</Label>
                  <Input size="sm" placeholder="e.g. Medical License" value={newDocName} onChange={(e) => { setNewDocName(e.target.value); setPendingUpload(null) }} />
                  {newDocName.trim() && <Text fontSize="xs" color="gray.400" mt={1}>Type: <b>{toSlug(newDocName)}</b></Text>}
                </Box>
                <Box>
                  <Label required>File</Label>
                  {pendingUpload ? (
                    <Box p={2} border="1px solid" borderColor="green.200" borderRadius="md" bg="green.50" display="flex" justifyContent="space-between" alignItems="center">
                      <Text fontSize="sm">{pendingUpload.file.name}</Text>
                      <Button size="xs" variant="ghost" colorScheme="red" onClick={() => setPendingUpload(null)}>✕</Button>
                    </Box>
                  ) : (
                    <Button size="sm" variant="outline" w="full" isDisabled={!newDocName.trim()} onClick={() => newDocFileRef.current?.click()}>Choose File</Button>
                  )}
                  <input type="file" accept=".pdf,image/*" hidden ref={newDocFileRef}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) { const slug = toSlug(newDocName); if (!slug) { ShowAlert("warning", "Enter a document name"); return } setPendingUpload({ docType: slug, file: f }) }; e.target.value = "" }} />
                </Box>
              </ModalBody>
              <ModalFooter gap={2}>
                <Button size="sm" variant="ghost" onClick={() => { setAddModalOpen(false); setNewDocName(""); setPendingUpload(null) }}>Cancel</Button>
                <Button size="sm" colorScheme="blue" isDisabled={!pendingUpload} isLoading={!!uploadingId} loadingText="Uploading" onClick={confirmAddDoc}>Upload</Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          {/* Sidebar */}
          <Box position="sticky" top={4} alignSelf="start">
            <Box border="1px solid" borderColor="gray.200" borderRadius="xl" p={6} bg="white" shadow="sm">
              <Text fontWeight="bold" fontSize="md" mb={6}>Verification Progress</Text>
              <Box display="flex" justifyContent="center" mb={6}>
                <Box position="relative" w="128px" h="128px">
                  <svg width="128" height="128" viewBox="0 0 100 100" style={{ transform: "rotate(-90deg)" }}>
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#e1e2ec" strokeWidth="8" />
                    <circle cx="50" cy="50" r="45" fill="none" stroke="#003d9b" strokeWidth="8" strokeLinecap="round"
                      strokeDasharray={circumference} strokeDashoffset={offset} style={{ transition: "stroke-dashoffset 0.4s" }} />
                  </svg>
                  <Box position="absolute" top={0} left={0} right={0} bottom={0} display="flex" alignItems="center" justifyContent="center">
                    <Text fontWeight="bold" fontSize="xl" lineHeight={1}>{verifiedCount}<Text as="span" fontSize="sm" color="gray.400">/{total}</Text></Text>
                  </Box>
                </Box>
              </Box>
              <Text fontSize="sm" color="gray.500" textAlign="center" mb={6}>{verifiedCount} out of {total} documents verified.</Text>
              <Box display="flex" flexDir="column" gap={3}>
                {docs.map((doc) => {
                  const p = PROGRESS_CFG[doc.status]
                  return (
                    <Box key={doc.id} display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={2}><Text fontSize="sm" color={p.color}>{p.icon}</Text><Text fontSize="sm">{doc.title}</Text></Box>
                      <Text fontSize="xs" fontWeight="semibold" color={p.color}>{p.statusLabel}</Text>
                    </Box>
                  )
                })}
              </Box>
              <Box mt={6} pt={4} borderTop="1px solid" borderColor="gray.100">
                <Button w="full" colorScheme="blue" isDisabled={verifiedCount < total} onClick={onGoToVerification}>Proceed to Verification →</Button>
                <Text fontSize="xs" color="gray.400" textAlign="center" mt={2}>All documents must be verified to proceed.</Text>
              </Box>
            </Box>
          </Box>
        </React.Fragment>
      )}
    </Box>
  )
}
